/* ==========================================================================
   3afret Electronics — theme.js
   Core interactions: overlays/drawers, mobile nav, sliders, quantity,
   cart (AJAX + drawer), predictive search, toasts, reveal.
   No third-party libraries.
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------ */
  const Theme = (window.Theme = window.Theme || {});

  Theme.routes = Object.assign(
    {
      root: '/',
      cart_add_url: '/cart/add',
      cart_change_url: '/cart/change',
      cart_update_url: '/cart/update',
      cart_url: '/cart',
      predictive_search_url: '/search/suggest'
    },
    window.Shopify && window.Shopify.routes ? window.Shopify.routes : {},
    window.themeRoutes || {}
  );

  Theme.strings = Object.assign(
    {
      addedToCart: 'Added to cart',
      addToCartError: 'Could not add this item to the cart.',
      soldOut: 'Sold out',
      unavailable: 'Unavailable',
      addToCart: 'Add to Cart',
      availability: 'Availability',
      inStock: 'In stock',
      outOfStock: 'Out of stock',
      brand: 'Brand',
      price: 'Price',
      from: 'From',
      to: 'To'
    },
    window.themeStrings || {}
  );

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  Theme.escapeHTML = function (value) {
    return String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );
  };

  // Safari can occasionally surface a text/SVG node as event.target. Keep
  // delegated interactions safe by normalising to an element before calling
  // closest().
  Theme.closest = function (target, selector) {
    let node = target;
    while (node) {
      if (node.nodeType === 1) {
        if (typeof node.matches === 'function' && node.matches(selector)) return node;
        if (typeof node.closest === 'function') {
          const found = node.closest(selector);
          if (found) return found;
        }
      }
      node = node.parentElement || node.parentNode;
    }
    return null;
  };

  Theme.debounce = function (fn, wait) {
    let t;
    return function () {
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  Theme.getSectionHTML = function (html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  };

  Theme.formatMoney = function (cents) {
    const format = window.themeMoneyFormat || '{{amount}}';
    const value = (cents / 100).toLocaleString(document.documentElement.lang || 'en', {
      minimumFractionDigits: format.indexOf('no_decimals') > -1 ? 0 : 2,
      maximumFractionDigits: format.indexOf('no_decimals') > -1 ? 0 : 2
    });
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  /* ------------------------------------------------------------------
     Scroll lock (shared counter so nested overlays behave)
     ------------------------------------------------------------------ */
  let scrollLocks = 0;
  let savedScrollY = 0;

  // Scroll lock without moving the page.
  //
  // The previous lock pinned the body with `position: fixed; top: -scrollY`.
  // iOS Safari then maps touches on fixed overlays against the shifted body
  // rather than the viewport, so every tap inside an open modal or drawer
  // landed on the wrong element once the page had been scrolled — the close
  // button and Add to cart both stopped responding. Leaving the body in place
  // and hiding overflow keeps coordinates honest, and removes the scroll jump
  // when the overlay closes.
  Theme.lockScroll = function () {
    if (scrollLocks === 0) {
      savedScrollY = window.scrollY;
      document.documentElement.classList.add('scroll-locked');
      document.body.classList.add('overflow-hidden');
      // Clear anything a previously cached page left behind.
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    }
    scrollLocks += 1;
  };

  Theme.unlockScroll = function () {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) {
      document.documentElement.classList.remove('scroll-locked');
      document.body.classList.remove('overflow-hidden');
    }
  };

  /* ------------------------------------------------------------------
     Focus trap
     ------------------------------------------------------------------ */
  function trapFocus(container, opener) {
    const nodes = () => Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    const first = nodes()[0];
    if (first) {
      // Delay so the CSS transition has started and the element is focusable.
      // preventScroll: the page is no longer pinned, so a plain focus() would
      // scroll the overlay's first control into view and move the page underneath.
      requestAnimationFrame(() => first.focus({ preventScroll: true }));
    }
    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      const items = nodes();
      if (!items.length) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    }
    container.addEventListener('keydown', onKeydown);
    return function release() {
      container.removeEventListener('keydown', onKeydown);
      if (opener && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    };
  }
  Theme.trapFocus = trapFocus;

  /* ------------------------------------------------------------------
     Overlay controller — drawers, modals, search overlay
     ------------------------------------------------------------------ */
  const Overlays = {
    open: null,
    releaseFocus: null,

    show(id, opener) {
      const target = document.getElementById(id);
      if (!target) return;
      if (this.open === target) return;

      // Replacing an open overlay keeps the existing scroll lock, so we must
      // not take a second one — otherwise the body never unlocks.
      const alreadyLocked = Boolean(this.open);
      if (this.open) this.hide(true);

      this.open = target;
      target.classList.add('is-open');
      target.removeAttribute('aria-hidden');

      const backdrop = target.dataset.overlay ? document.getElementById(target.dataset.overlay) : null;
      if (backdrop) backdrop.classList.add('is-open');

      if (!alreadyLocked) Theme.lockScroll();
      this.releaseFocus = trapFocus(target, opener);
      document.querySelectorAll(`[data-overlay-open="${id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      target.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    },

    hide(skipUnlock) {
      const target = this.open;
      if (!target) return;
      target.classList.remove('is-open');
      target.setAttribute('aria-hidden', 'true');

      const backdrop = target.dataset.overlay ? document.getElementById(target.dataset.overlay) : null;
      if (backdrop) backdrop.classList.remove('is-open');

      document.querySelectorAll(`[data-overlay-open="${target.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      this.open = null;
      if (this.releaseFocus) {
        this.releaseFocus();
        this.releaseFocus = null;
      }
      if (!skipUnlock) Theme.unlockScroll();
      target.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    },

    toggle(id, opener) {
      if (this.open && this.open.id === id) this.hide();
      else this.show(id, opener);
    }
  };
  Theme.overlays = Overlays;

  function handleOverlayClose(event) {
    // Close controls must win even when nested inside another interactive
    // element (for example the dynamically loaded Choose Options modal).
    const close = Theme.closest(event.target, '[data-overlay-close]');
    if (!close) return false;
    event.preventDefault();
    event.stopPropagation();
    // If a dynamic section replacement briefly cleared the controller's
    // reference, recover the visible dialog from the clicked close button.
    if (!Overlays.open) {
      const visible = close.closest('.modal.is-open, .drawer.is-open');
      if (visible) Overlays.open = visible;
    }
    Overlays.hide();
    return true;
  }

  document.addEventListener('click', (event) => {
    if (handleOverlayClose(event)) return;

    const opener = Theme.closest(event.target, '[data-overlay-open]');
    if (opener) {
      event.preventDefault();
      Overlays.toggle(opener.dataset.overlayOpen, opener);
      return;
    }
    // Clicking the dimmed backdrop closes. The click must land inside the open
    // modal but outside its dialog box — testing "outside the dialog" alone
    // would also match the trigger that just opened it, closing it instantly.
    const el = event.target && event.target.nodeType === 1 ? event.target : null;
    if (el && (el.classList.contains('overlay') || el.classList.contains('modal'))) {
      Overlays.hide();
      return;
    }
    const openOverlay = Overlays.open;
    if (openOverlay && openOverlay.classList.contains('modal') && el && openOverlay.contains(el) &&
        !Theme.closest(el, '.modal__dialog')) {
      Overlays.hide();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && Overlays.open) Overlays.hide();
  });

  /* ------------------------------------------------------------------
     Touch close net for every overlay
     ------------------------------------------------------------------
     iOS Safari does not always deliver the synthetic click for a tap on a
     control inside a fixed overlay, which left close buttons dead on pages
     carrying more than one drawer. The touch events always arrive, so resolve
     the target from the finger's own coordinates rather than from the element
     Safari reports. Touch only: mouse clicks keep using the delegation above.
  */
  const TAP_SLOP = 10;
  let touchStart = null;
  let startedOutsidePanel = false;

  function openOverlayEl() {
    return Overlays.open || document.querySelector('.modal.is-open, .drawer.is-open');
  }

  function panelOf(overlay) {
    return overlay.querySelector('.modal__dialog') || overlay;
  }

  function pointInRect(x, y, rect, pad) {
    return rect.width > 0 && rect.height > 0 &&
      x >= rect.left - pad && x <= rect.right + pad &&
      y >= rect.top - pad && y <= rect.bottom + pad;
  }

  function outsidePanel(overlay, x, y) {
    return !pointInRect(x, y, panelOf(overlay).getBoundingClientRect(), 0);
  }

  function closeControlAt(overlay, x, y) {
    const controls = overlay.querySelectorAll('[data-overlay-close]');
    for (let i = 0; i < controls.length; i += 1) {
      if (pointInRect(x, y, controls[i].getBoundingClientRect(), 8)) return controls[i];
    }
    return null;
  }

  document.addEventListener('touchstart', (event) => {
    const touch = event.changedTouches && event.changedTouches[0];
    const overlay = touch ? openOverlayEl() : null;
    touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
    startedOutsidePanel = Boolean(overlay) && Boolean(touch) && outsidePanel(overlay, touch.clientX, touch.clientY);
  }, true);

  document.addEventListener('touchend', (event) => {
    const touch = event.changedTouches && event.changedTouches[0];
    const overlay = openOverlayEl();
    if (!touch || !overlay || !touchStart) return;
    // A drag that scrolled the panel is not a tap.
    if (Math.abs(touch.clientX - touchStart.x) > TAP_SLOP || Math.abs(touch.clientY - touchStart.y) > TAP_SLOP) return;
    const onClose = closeControlAt(overlay, touch.clientX, touch.clientY);
    const onBackdrop = startedOutsidePanel && outsidePanel(overlay, touch.clientX, touch.clientY);
    if (!onClose && !onBackdrop) return;
    // Also stops the synthetic click landing on whatever is revealed beneath.
    event.preventDefault();
    if (Overlays.open !== overlay) Overlays.open = overlay;
    Overlays.hide();
  }, true);

  /* ------------------------------------------------------------------
     Toasts
     ------------------------------------------------------------------ */
  const CHECK_ICON =
    '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10.5 8 14.5 16 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  Theme.toast = function (message, icon) {
    let stack = document.querySelector('.toast-stack');
    if (!stack) {
      stack = document.createElement('div');
      stack.className = 'toast-stack';
      stack.setAttribute('role', 'status');
      stack.setAttribute('aria-live', 'polite');
      document.body.appendChild(stack);
    }
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.innerHTML = (icon === false ? '' : CHECK_ICON) + '<span></span>';
    toast.querySelector('span').textContent = message;
    stack.appendChild(toast);
    setTimeout(() => {
      toast.style.transition = 'opacity 200ms linear';
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 220);
    }, 3200);
  };

  /* ------------------------------------------------------------------
     Header: sticky shadow
     ------------------------------------------------------------------ */
  const header = document.querySelector('.header-wrapper');
  if (header && header.classList.contains('header-wrapper--sticky')) {
    const onScroll = () => {
      header.classList.toggle('header-wrapper--scrolled', window.scrollY > 6);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    /* Only the menu row stays pinned: the wrapper sticks at a negative offset
       equal to the top bar's height, so the logo and search scroll away. */
    const topBar = header.querySelector('.header');
    const nav = header.querySelector('.header-nav');
    const measure = () => {
      if (!topBar || !nav) return;
      /* Distance from the wrapper's top edge to the nav row: exact whatever the
         padding around the bar. Both move together, so it is stable while stuck. */
      /* offsetTop is in layout pixels, like the sticky `top` value; bounding
         rects are not once the page is rendered at 90%. */
      let offset = 0;
      for (let el = nav; el && el !== header; el = el.offsetParent) offset += el.offsetTop;
      (header.closest('.header-section') || header).style.setProperty('--hdr-top-height', `${Math.max(0, offset)}px`);
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });
    if ('ResizeObserver' in window && topBar) new ResizeObserver(measure).observe(topBar);
  }

  /* ------------------------------------------------------------------
     Mobile nav: collapsible submenus
     ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
        const toggle = Theme.closest(event.target, '.mobile-nav__toggle');
    if (!toggle) return;
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
    if (submenu) submenu.classList.toggle('is-open', !expanded);
  });

  /* ------------------------------------------------------------------
     Announcement bar rotator
     ------------------------------------------------------------------ */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.announcement-bar__item'));
      if (this.items.length < 2) return;
      this.index = 0;
      this.interval = parseInt(this.dataset.interval || '5', 10) * 1000;

      this.querySelectorAll('.announcement-bar__nav').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.stop();
          this.go(this.index + (btn.dataset.direction === 'next' ? 1 : -1));
        });
      });

      if (this.dataset.autoplay === 'true') this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('focusin', () => this.stop());
    }

    disconnectedCallback() {
      this.stop();
    }

    go(next) {
      const total = this.items.length;
      this.index = ((next % total) + total) % total;
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === this.index));
    }

    start() {
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ------------------------------------------------------------------
     Slider (horizontal product / category carousels)
     ------------------------------------------------------------------ */
  class SliderCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      this.dotsList = this.querySelector('[data-slider-dots]');

      this.onScroll = Theme.debounce(() => this.update(), 60);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      // A resize can change how many slides fit, so the dots must be rebuilt.
      this.onResize = Theme.debounce(() => {
        this.buildDots();
        this.update();
      }, 120);
      window.addEventListener('resize', this.onResize, { passive: true });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByPage(1));

      // Build once now, then again when the track actually has a size.
      // ResizeObserver is used rather than requestAnimationFrame because rAF
      // never fires in a hidden tab, which would leave the dots missing.
      this.buildDots();
      this.update();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.buildDots();
          this.update();
        });
        this.resizeObserver.observe(this.track);
      } else {
        setTimeout(() => {
          this.buildDots();
          this.update();
        }, 0);
      }
      this.initAutoplay();
      if (this.loop) this.initLoopGestures();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /* Opt-in via data-autoplay. Pauses on interaction and when the tab is
       hidden, and is skipped entirely for reduced-motion users. */
    initAutoplay() {
      if (this.dataset.autoplay !== 'true') return;
      if (this.slides.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.interval = Math.max(2, parseInt(this.dataset.interval || '5', 10)) * 1000;
      this.startAutoplay();

      ['mouseenter', 'focusin', 'touchstart', 'pointerdown'].forEach((evt) =>
        this.addEventListener(evt, () => this.stopAutoplay(), { passive: true })
      );
      ['mouseleave', 'focusout'].forEach((evt) =>
        this.addEventListener(evt, () => this.startAutoplay())
      );
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });
    }

    startAutoplay() {
      if (!this.interval) return;
      this.stopAutoplay();
      this.timer = setInterval(() => this.advance(), this.interval);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    advance() {
      const pos = Math.abs(this.track.scrollLeft);
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (pos >= max) this.track.scrollTo({ left: 0, behavior: 'smooth' });
      else this.scrollByPage(1);
    }

    get slides() {
      return Array.from(this.track.children);
    }

    get slideStep() {
      const slides = this.slides;
      if (slides.length < 2) return this.track.clientWidth || 1;
      // Distance between two slide starts, direction-agnostic.
      const step = Math.abs(slides[1].offsetLeft - slides[0].offsetLeft);
      return step || this.track.clientWidth || 1;
    }

    get perView() {
      const step = this.slideStep;
      const width = this.track.clientWidth;
      // Guard against measuring before layout: 0/0 would yield NaN and silently
      // collapse the dot count to zero.
      if (!step || !width) return 1;
      return Math.max(1, Math.round(width / step));
    }

    get pageCount() {
      return Math.max(1, Math.ceil(this.slides.length / this.perView));
    }

    /* Opt-in via data-loop="true": arrows, swipes and trackpad scrolls that
       run past the last slide come back around to the first (and vice versa). */
    get loop() {
      return this.dataset.loop === 'true';
    }

    get atStart() {
      return Math.abs(this.track.scrollLeft) <= 2;
    }

    get atEnd() {
      return Math.abs(this.track.scrollLeft) >= this.track.scrollWidth - this.track.clientWidth - 2;
    }

    scrollToStart() {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
    }

    scrollToEnd() {
      const rtl = getComputedStyle(this).direction === 'rtl';
      const left = (this.track.scrollWidth - this.track.clientWidth) * (rtl ? -1 : 1);
      this.track.scrollTo({ left, behavior: 'smooth' });
    }

    scrollByPage(direction) {
      if (this.loop && this.slides.length > 1) {
        if (direction > 0 && this.atEnd) { this.scrollToStart(); return; }
        if (direction < 0 && this.atStart) { this.scrollToEnd(); return; }
      }
      const rtl = getComputedStyle(this).direction === 'rtl';
      const amount = this.slideStep * this.perView * direction * (rtl ? -1 : 1);
      this.track.scrollBy({ left: amount, behavior: 'smooth' });
    }

    /* Scroll-snap stops the track at the last slide, so a swipe or wheel
       gesture that continues past the end is caught here and wrapped. */
    initLoopGestures() {
      const forwardOf = (dx) => (getComputedStyle(this).direction === 'rtl' ? dx > 0 : dx < 0);
      let startX = 0;
      let startY = 0;
      let wasAtEnd = false;
      let wasAtStart = false;
      this.track.addEventListener('touchstart', (event) => {
        const t = event.changedTouches[0];
        startX = t.clientX;
        startY = t.clientY;
        wasAtEnd = this.atEnd;
        wasAtStart = this.atStart;
      }, { passive: true });
      this.track.addEventListener('touchend', (event) => {
        const t = event.changedTouches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        // Finger moving toward the end of the strip = "next".
        if (forwardOf(dx) && wasAtEnd && this.atEnd) this.scrollToStart();
        else if (!forwardOf(dx) && wasAtStart && this.atStart) this.scrollToEnd();
      }, { passive: true });

      let wheelLock = 0;
      this.track.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaX) < 20 || Math.abs(event.deltaX) < Math.abs(event.deltaY)) return;
        const now = Date.now();
        if (now - wheelLock < 900) return;
        // Wheel deltaX is positive when content should move left (next) in LTR.
        const forward = forwardOf(-event.deltaX);
        if (forward && this.atEnd) { wheelLock = now; this.scrollToStart(); }
        else if (!forward && this.atStart) { wheelLock = now; this.scrollToEnd(); }
      }, { passive: true });
    }

    buildDots() {
      if (!this.dotsList) return;
      const pages = this.pageCount;
      // Skip a rebuild when nothing changed, so clicking a dot doesn't wipe it.
      if (this.builtPages === pages) return;
      this.builtPages = pages;
      this.dotsList.innerHTML = '';
      if (pages < 2) return;
      for (let i = 0; i < pages; i += 1) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('aria-label', `Go to slide group ${i + 1}`);
        btn.addEventListener('click', () => {
          const rtl = getComputedStyle(this).direction === 'rtl';
          const left = this.slideStep * this.perView * i * (rtl ? -1 : 1);
          this.track.scrollTo({ left, behavior: 'smooth' });
        });
        li.appendChild(btn);
        this.dotsList.appendChild(li);
      }
    }

    update() {
      const rtl = getComputedStyle(this).direction === 'rtl';
      const pos = Math.abs(this.track.scrollLeft);
      const max = this.track.scrollWidth - this.track.clientWidth - 2;

      if (this.prevBtn) this.prevBtn.disabled = !this.loop && pos <= 2;
      if (this.nextBtn) this.nextBtn.disabled = !this.loop && pos >= max;

      if (this.dotsList) {
        const buttons = this.dotsList.querySelectorAll('button');
        if (buttons.length) {
          const page = Math.round(pos / (this.slideStep * this.perView));
          buttons.forEach((btn, i) =>
            btn.setAttribute('aria-current', String(i === Math.min(page, buttons.length - 1)))
          );
        }
      }
      void rtl;
    }
  }
  customElements.define('slider-carousel', SliderCarousel);

  /**
   * Tabbed product carousels. Panels are pre-rendered and toggled, so switching
   * is instant. Follows the ARIA tabs pattern: one tab in the tab order, arrow
   * keys move between them, Home/End jump to the ends.
   */
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (this.tabs.length < 2) return;

      this.addEventListener('click', (event) => {
        const tab = Theme.closest(event.target, '[role="tab"]');
        if (tab) this.select(this.tabs.indexOf(tab));
      });

      this.addEventListener('keydown', (event) => {
        const current = this.tabs.indexOf(document.activeElement);
        if (current === -1) return;
        const last = this.tabs.length - 1;
        let next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = current === last ? 0 : current + 1;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = current === 0 ? last : current - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = last;
        if (next === null) return;
        event.preventDefault();
        this.select(next);
        this.tabs[next].focus();
      });
    }

    select(index) {
      if (index < 0 || index >= this.tabs.length) return;
      this.tabs.forEach((tab, i) => {
        const chosen = i === index;
        tab.setAttribute('aria-selected', String(chosen));
        tab.tabIndex = chosen ? 0 : -1;
        const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
        if (!panel) return;
        panel.hidden = !chosen;
        if (!chosen) return;
        // The slider measured itself while hidden, where every width is 0, so it
        // has to rebuild once the panel is actually laid out.
        const slider = panel.querySelector('slider-carousel');
        if (slider && typeof slider.buildDots === 'function') {
          slider.buildDots();
          slider.update();
        }
      });
    }
  }
  customElements.define('product-tabs', ProductTabs);

  /* ------------------------------------------------------------------
     Quantity stepper
     ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          const step = btn.dataset.action === 'increase' ? 1 : -1;
          const min = parseInt(this.input.min || '1', 10);
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
          if (String(next) === this.input.value) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      this.input.addEventListener('change', () => this.syncButtons());
      this.syncButtons();
    }

    syncButtons() {
      const value = parseInt(this.input.value, 10) || 1;
      const min = parseInt(this.input.min || '1', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const dec = this.querySelector('[data-action="decrease"]');
      const inc = this.querySelector('[data-action="increase"]');
      if (dec) dec.disabled = value <= min;
      if (inc) inc.disabled = value >= max;
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------
     Cart
     ------------------------------------------------------------------ */
  // Minimum order (Theme settings › Cart): the cart sections render the form with
  // data-min-order-block and a disabled Checkout button while below the minimum. This
  // guard also stops a submit that slips through (e.g. the button re-enabled by script).
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !form.hasAttribute || !form.hasAttribute('data-min-order-block')) return;
    if (!e.submitter || e.submitter.name !== 'checkout') return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const scope = form.closest('.drawer, .cart-summary-card, aside, main') || document;
    const note = scope.querySelector('[data-min-order-below]');
    if (note) { note.scrollIntoView({ behavior: 'smooth', block: 'center' }); note.classList.add('is-flash'); setTimeout(() => note.classList.remove('is-flash'), 900); }
  }, true);

  const Cart = {
    sectionsToRender() {
      const names = ['cart-icon-bubble'];
      if (document.getElementById('cart-drawer')) names.push('cart-drawer');
      const main = document.getElementById('main-cart-section');
      if (main) names.push(main.dataset.sectionId || 'main-cart');
      return names;
    },

    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(
          Object.assign({ sections: this.sectionsToRender().join(','), sections_url: window.location.pathname }, body)
        )
      });
      const data = await response.json();
      if (!response.ok) {
        const message = data && (data.description || data.message) ? data.description || data.message : Theme.strings.addToCartError;
        throw new Error(message);
      }
      // Add-to-cart log (assets/cart-log.js), only present when enabled in theme settings.
      if (url.indexOf(Theme.routes.cart_add_url) === 0 && window.GTCartLogReport) window.GTCartLogReport(data, body);
      return data;
    },

    renderSections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((name) => {
        const html = sections[name];
        if (typeof html !== 'string') return;

        if (name === 'cart-icon-bubble') {
          const parsedInner = Theme.getSectionHTML(html, '#shopify-section-cart-icon-bubble');
          const inner = parsedInner === null ? html : parsedInner;
          document.querySelectorAll('#cart-icon-bubble').forEach((target) => {
            target.innerHTML = inner;
          });
          return;
        }

        const inner = Theme.getSectionHTML(html, '[data-section-contents]');
        const directRoot = document.getElementById(name);
        const container = (directRoot ? directRoot.querySelector('[data-section-contents]') : null) ||
          document.querySelector(`[data-section-id="${name}"] [data-section-contents]`) ||
          document.querySelector(`#shopify-section-${name} [data-section-contents]`);
        if (inner !== null && container) container.innerHTML = inner;
      });
      Cart.syncCounters();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },

    // The header bubble is re-rendered by the Section Rendering API, but the
    // mobile bottom bar lives in the header section and is not, so its badge
    // stayed at the page-load value until a refresh. Mirror the fresh count
    // onto every [data-cart-count] element.
    syncCounters() {
      const bubble = document.querySelector('#cart-icon-bubble .header__icon-count');
      let count = bubble ? parseInt(bubble.textContent.trim(), 10) : NaN;
      if (Number.isNaN(count)) {
        const label = document.querySelector('#cart-icon-bubble [data-cart-count-text]');
        const match = label && label.textContent.match(/\d+/);
        count = match ? Number(match[0]) : 0;
      }
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count > 0 ? String(count) : '';
      });
    },

    async add(items, opener) {
      const data = await this.request(Theme.routes.cart_add_url + '.js', { items });
      this.renderSections(data.sections);
      // A variant add from Choose Options should transition cleanly to the
      // cart drawer; close the quick-view modal before opening the cart.
      if (Theme.overlays.open && Theme.overlays.open.id === 'quick-view-modal') Theme.overlays.hide();
      this.openDrawer(opener);
      return data;
    },

    async change(payload) {
      const data = await this.request(Theme.routes.cart_change_url + '.js', payload);
      this.renderSections(data.sections);
      // Refresh the rendered cart sections once more from the cart endpoint.
      // This covers mobile drawers and pages where the section-rendering
      // response can contain a stale section wrapper after a line is removed.
      try {
        const params = new URLSearchParams({
          sections: this.sectionsToRender().join(','),
          sections_url: window.location.pathname
        });
        const response = await fetch(`${Theme.routes.cart_url}?${params.toString()}`, {
          headers: { Accept: 'application/json' }
        });
        if (response.ok) {
          const refreshed = await response.json();
          this.renderSections(refreshed.sections || refreshed);
        }
      } catch (error) {
        // The initial AJAX response has already updated the cart; a failed
        // follow-up refresh should not block the customer's cart action.
      }
      return data;
    },

    openDrawer(opener) {
      const drawer = document.getElementById('cart-drawer');
      if (drawer && document.body.dataset.cartType === 'drawer') {
        Theme.overlays.show('cart-drawer', opener);
      } else {
        Theme.toast(Theme.strings.addedToCart);
      }
    }
  };
  Theme.cart = Cart;

  /* Product form — AJAX add to cart (product page, quick add, quick view) */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      // Dynamic checkout buttons submit natively.
      if (event.submitter && event.submitter.name === 'checkout') return;
      event.preventDefault();

      const button = this.form.querySelector('[data-add-to-cart]') || event.submitter;
      const buyNow = event.submitter && event.submitter.hasAttribute('data-buy-now');
      const target = buyNow ? event.submitter : button;
      const idInput = this.form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) return;

      const qtyInput = this.form.querySelector('[name="quantity"]');
      const item = {
        id: Number(idInput.value),
        quantity: qtyInput ? Number(qtyInput.value) || 1 : 1
      };

      const properties = {};
      this.form.querySelectorAll('[name^="properties["]').forEach((input) => {
        const key = input.name.slice('properties['.length, -1);
        if (input.value) properties[key] = input.value;
      });
      if (Object.keys(properties).length) item.properties = properties;

      this.setLoading(target, true);
      this.setError('');

      try {
        if (buyNow) {
          await Cart.request(Theme.routes.cart_add_url + '.js', { items: [item] });
          // Via the cart, not straight to /checkout: the cart's "Deliver to"
          // step is required (assets/delivery-area.js). With a choice already
          // made, the cart page continues to checkout on its own.
          window.location.href = Theme.routes.root.replace(/\/$/, '') + '/cart?buy_now=1';
          return;
        }
        await Cart.add([item], target);
      } catch (error) {
        this.setError(error.message || Theme.strings.addToCartError);
      } finally {
        this.setLoading(target, false);
      }
    }

    setLoading(button, state) {
      if (!button) return;
      button.classList.toggle('btn--loading', state);
      button.toggleAttribute('aria-disabled', state);
    }

    setError(message) {
      if (!this.errorEl) {
        if (message) Theme.toast(message, false);
        return;
      }
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }
  customElements.define('product-form', ProductForm);

  /* Cart line item quantity / remove */
  document.addEventListener('change', (event) => {
    const input = Theme.closest(event.target, '[data-cart-quantity]');
    if (!input) return;
    const line = input.dataset.cartQuantity;
    const quantity = Number(input.value);
    const root = input.closest('[data-cart-root]');
    if (root) root.classList.add('facets-form--loading');
    Cart.change({ line: Number(line), quantity })
      .catch((error) => {
        Theme.toast(error.message, false);
      })
      .finally(() => {
        if (root) root.classList.remove('facets-form--loading');
      });
  });

  document.addEventListener('click', (event) => {
    const remove = Theme.closest(event.target, '[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    const root = remove.closest('[data-cart-root]');
    if (root) root.classList.add('facets-form--loading');
    Cart.change({ line: Number(remove.dataset.cartRemove), quantity: 0 })
      .catch((error) => {
        Theme.toast(error.message, false);
      })
      .finally(() => {
        if (root) root.classList.remove('facets-form--loading');
      });
  });

  /* Quick add buttons on product cards (single-variant products) */
  document.addEventListener('click', async (event) => {
    // Safari may expose a text/SVG node as the click target; walk up to the
    // element carrying the delegated action instead of assuming closest().
    const button = Theme.closest(event.target, '[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    const id = Number(button.dataset.quickAdd);
    if (!id) return;
    button.classList.add('btn--loading');
    button.setAttribute('aria-disabled', 'true');
    try {
      await Cart.add([{ id, quantity: 1 }], button);
    } catch (error) {
      Theme.toast(error.message || Theme.strings.addToCartError, false);
    } finally {
      button.classList.remove('btn--loading');
      button.removeAttribute('aria-disabled');
    }
  });

  /* Cart note */
  document.addEventListener(
    'change',
    Theme.debounce((event) => {
      const note = Theme.closest(event.target, '[data-cart-note]');
      if (!note) return;
      fetch(Theme.routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.value })
      });
    }, 500)
  );

  /* ------------------------------------------------------------------
     Predictive search
     ------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-search-results]');
      this.reset = this.querySelector('.search-field__reset');
      if (!this.input || !this.panel) return;

      this.cache = new Map();
      this.abort = null;

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('autocomplete', 'off');

      this.input.addEventListener('input', Theme.debounce(() => this.onInput(), 250));
      this.input.addEventListener('focus', () => {
        // Focus alone never fetches or opens a fresh panel — it only restores
        // results the shopper already had. Fetching on focus dropped a panel on
        // top of the real grid whenever a pre-filled field took focus.
        const query = this.input.value.trim();
        if (query.length > 1 && this.cache.has(query)) this.render(this.cache.get(query));
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));

      if (this.reset) {
        this.reset.addEventListener('click', () => {
          this.input.value = '';
          this.input.focus();
          this.close();
          this.reset.hidden = true;
        });
      }

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });
    }

    async onInput() {
      const query = this.input.value.trim();
      if (this.reset) this.reset.hidden = query.length === 0;

      if (query.length < 2) {
        this.close();
        return;
      }

      if (this.cache.has(query)) {
        this.render(this.cache.get(query));
        return;
      }

      this.panel.innerHTML = '<div class="search-results__loading" role="status"><span class="visually-hidden">Loading</span></div>';
      this.openPanel();

      if (this.abort) this.abort.abort();
      this.abort = new AbortController();

      const params = new URLSearchParams({
        q: query,
        'resources[type]': this.dataset.types || 'product',
        'resources[limit]': this.dataset.limit || '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag'
      });
      params.append('section_id', 'predictive-search');

      try {
        const response = await fetch(`${Theme.routes.predictive_search_url}?${params}`, { signal: this.abort.signal });
        const text = await response.text();
        const parsedHtml = Theme.getSectionHTML(text, '#shopify-section-predictive-search');
        const html = parsedHtml === null ? text : parsedHtml;
        this.cache.set(query, html);
        this.render(html);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    render(html) {
      this.panel.innerHTML = html;
      this.openPanel();
    }

    openPanel() {
      this.panel.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.activeIndex = -1;
    }

    get options() {
      return Array.from(this.panel.querySelectorAll('[data-search-result]'));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;

      const options = this.options;
      if (!options.length) return;

      if (event.key === 'Enter') {
        if (this.activeIndex >= 0 && options[this.activeIndex]) {
          event.preventDefault();
          options[this.activeIndex].click();
        }
        return;
      }

      event.preventDefault();
      const dir = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = ((this.activeIndex == null ? -1 : this.activeIndex) + dir + options.length) % options.length;
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === this.activeIndex)));
      options[this.activeIndex].scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* Mirror the category dropdown into its hidden input. Done on change, so the
     submitted state is settled well before the form serialises — toggling it
     during submit left a window where a second submit could drop the filter. */
  document.addEventListener('change', (event) => {
    const scope = Theme.closest(event.target, '[data-search-scope]');
    if (!scope) return;
    const form = scope.closest('form');
    const field = form && form.querySelector('[data-search-scope-value]');
    if (!field) return;
    field.value = scope.value;
    // A disabled control is not submitted, which is what keeps an "All" search
    // from carrying an empty filter parameter.
    field.disabled = scope.value === '';
  });

  /**
   * Shown on the search results page when full-text search returns nothing.
   * Full-text /search and predictive /search/suggest are separate engines, so a
   * short fragment can match in the dropdown and miss here. This re-asks the
   * predictive endpoint for the same term and lists whatever it finds, so the
   * shopper is not dead-ended on a query that visibly had matches.
   */
  class SearchFallback extends HTMLElement {
    async connectedCallback() {
      const query = (this.dataset.query || '').trim();
      const target = this.querySelector('[data-fallback-results]');
      if (!query || query.length < 2 || !target) return;

      // Hiding is done here rather than in Liquid on purpose: with JS disabled or
      // broken the shopper must still get the normal "no results" message, so the
      // page ships visible and only this element takes it away.
      this.hideEmptyState();

      // 'predictive-search' renders compact rows, 'predictive-search-grid'
      // renders full product cards. The section is chosen by the theme setting.
      const section = this.dataset.section || 'predictive-search';

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': this.dataset.limit || '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag'
      });
      params.append('section_id', section);

      let html;
      try {
        const response = await fetch(`${Theme.routes.predictive_search_url}?${params}`);
        if (!response.ok) return this.restoreEmptyState();
        const text = await response.text();
        const parsedHtml = Theme.getSectionHTML(text, `#shopify-section-${section}`);
        html = parsedHtml === null ? text : parsedHtml;
      } catch (error) {
        return this.restoreEmptyState();
      }

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      // Drop the dropdown's own "View all results" link — it points back at the
      // query that just returned nothing. (List layout only; the grid has none.)
      parsed.querySelectorAll('.search-results__footer').forEach((node) => node.remove());
      const results = parsed.querySelectorAll('.search-result, [data-product-card]');
      if (!results.length) return this.restoreEmptyState();

      target.innerHTML = parsed.body.innerHTML;
      this.hidden = false;
      this.initWishlist(target);
      this.buildFilters(target);
    }

    /**
     * Wishlist Hero renders its hearts by scanning the page once on load, so
     * cards fetched afterwards come back as empty placeholders. Its SDK exposes
     * an explicit initialiser, which we hand each new button.
     * https://d2uzwr8l81w3zz.cloudfront.net/InitializeAddToWishListItem.html
     */
    initWishlist(container) {
      const buttons = Array.from(container.querySelectorAll('.wishlist-hero-custom-button'));
      if (!buttons.length) return;

      const initialise = () => {
        const sdk = window.WishListHero_SDK;
        if (!sdk || typeof sdk.InitializeAddToWishListButton !== 'function') return;

        buttons.forEach((node, index) => {
          // The SDK targets a button by class name, so each one needs its own.
          const marker = `fb-wlh-${index}-${node.dataset.wlhId || '0'}`;
          node.classList.add(marker);
          sdk.InitializeAddToWishListButton({
            ButtonClassElement: marker,
            ProductId: Number(node.dataset.wlhId),
            ProductLink: node.dataset.wlhLink,
            ProductVariantId: Number(node.dataset.wlhVariantid),
            ProductPrice: Number(node.dataset.wlhPrice),
            ProductTitle: node.dataset.wlhName,
            ProductImage: node.dataset.wlhImage,
            ButtonMode: node.dataset.wlhMode || 'icon_only'
          });
        });
      };

      // The SDK may already be up, or may still be loading.
      if (window.WishListHero_SDK) {
        initialise();
      } else {
        document.addEventListener('wishlist-hero-wishlist-sdk-ready', initialise, { once: true });
      }
    }

    /**
     * Filters are built from the suggested products because Shopify derives
     * search.filters from the result set, which is empty by definition here.
     * Client-side only, over at most the predictive API's 10 products.
     */
    buildFilters(target) {
      if (this.dataset.filters !== 'true') return;
      const scope = this.closest('facet-filters');
      // Two hosts, because the theme hides .facets-sidebar below 990px and puts
      // filters in a drawer there. Only one is ever visible; both are wired and
      // kept in sync so it does not matter which one the shopper touches.
      const hosts = [
        scope && scope.querySelector('[data-fallback-facets]'),
        this.querySelector('[data-fb-facets-mobile]')
      ].filter(Boolean);
      this.items = Array.from(target.querySelectorAll('[data-fb-item]'));
      if (!hosts.length || !this.items.length) return;

      const grid = target.querySelector('.search-fallback__grid');
      const currency = (grid && grid.dataset.currency) || '';
      const amounts = this.items.map((li) => parseFloat(li.dataset.amount) || 0);
      const maxAmount = Math.ceil(Math.max(...amounts));

      const groups = [];

      if (this.dataset.filterAvailability === 'true') {
        // Both options always render, as Shopify's own availability facet does —
        // a zero-count option is disabled rather than dropped, so the group does
        // not disappear when every product happens to share a stock state.
        const inStock = this.items.filter((li) => li.dataset.available === 'true').length;
        const outOfStock = this.items.length - inStock;
        groups.push({
          key: 'available',
          label: Theme.strings.availability,
          values: [
            { value: 'true', label: Theme.strings.inStock, count: inStock },
            { value: 'false', label: Theme.strings.outOfStock, count: outOfStock }
          ]
        });
      }

      if (this.dataset.filterBrand === 'true') {
        const counts = new Map();
        this.items.forEach((li) => {
          const vendor = (li.dataset.vendor || '').trim();
          if (vendor) counts.set(vendor, (counts.get(vendor) || 0) + 1);
        });
        if (counts.size) {
          groups.push({
            key: 'vendor',
            label: Theme.strings.brand,
            values: [...counts.entries()]
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([vendor, count]) => ({ value: vendor, label: vendor, count }))
          });
        }
      }

      const showPrice = this.dataset.filterPrice === 'true' && maxAmount > 0;
      if (!groups.length && !showPrice) return;

      const markup = (prefix, open) => `<form class="facets-form" data-fb-facets-form novalidate>
        ${groups.map((group, index) => `
          <details class="facet" ${open && index < 3 ? 'open' : ''}>
            <summary class="facet__summary">
              <span>${Theme.escapeHTML(group.label)}</span>
              <span class="facet__summary-end"></span>
            </summary>
            <div class="facet__content">
              <ul class="facet__list list-unstyled">
                ${group.values.map((value, i) => `
                  <li class="facet__item${value.count === 0 ? ' facet__item--disabled' : ''}">
                    <label for="${prefix}-${group.key}-${i}">
                      <input type="checkbox" id="${prefix}-${group.key}-${i}"
                        data-fb-key="${group.key}" value="${Theme.escapeHTML(value.value)}"
                        ${value.count === 0 ? 'disabled' : ''}>
                      <span class="facet__item-name">${Theme.escapeHTML(value.label)}</span>
                      <span class="facet__item-count">(${value.count})</span>
                    </label>
                  </li>`).join('')}
              </ul>
            </div>
          </details>`).join('')}
        ${showPrice ? `
          <details class="facet" ${open ? 'open' : ''}>
            <summary class="facet__summary">
              <span>${Theme.escapeHTML(Theme.strings.price)}</span>
              <span class="facet__summary-end"></span>
            </summary>
            <div class="facet__content">
              <div class="facet__price">
                <div class="facet__price-field">
                  <label for="${prefix}-price-min">${Theme.escapeHTML(Theme.strings.from)}</label>
                  <div class="facet__price-input-wrap">
                    <span class="facet__price-currency">${Theme.escapeHTML(currency)}</span>
                    <input type="number" inputmode="decimal" step="any" id="${prefix}-price-min"
                      data-fb-price="min" min="0" max="${maxAmount}" placeholder="0">
                  </div>
                </div>
                <div class="facet__price-field">
                  <label for="${prefix}-price-max">${Theme.escapeHTML(Theme.strings.to)}</label>
                  <div class="facet__price-input-wrap">
                    <span class="facet__price-currency">${Theme.escapeHTML(currency)}</span>
                    <input type="number" inputmode="decimal" step="any" id="${prefix}-price-max"
                      data-fb-price="max" min="0" max="${maxAmount}" placeholder="${maxAmount}">
                  </div>
                </div>
              </div>
            </div>
          </details>` : ''}
      </form>`;

      this.forms = [];
      hosts.forEach((host, index) => {
        const isSidebar = index === 0;
        host.innerHTML = markup(isSidebar ? 'fb-desktop' : 'fb-mobile', isSidebar);
        host.hidden = false;

        const form = host.querySelector('[data-fb-facets-form]');
        this.forms.push(form);
        form.addEventListener('change', () => this.applyFilters(form));
        form.addEventListener('input', Theme.debounce(() => this.applyFilters(form), 250));
        form.addEventListener('submit', (event) => event.preventDefault());

        if (host.dataset.style === 'horizontal') this.wireChips(host);
      });

      // Give the sidebar its track back — it was collapsed because the empty
      // result set rendered no native filters.
      const layout = scope && scope.querySelector('.collection-layout');
      if (layout) layout.classList.remove('collection-layout--full');
    }

    /**
     * Horizontal chips open a small panel each. Keep one open at a time and
     * close on an outside tap, which is what the shape leads people to expect.
     */
    wireChips(host) {
      const chips = Array.from(host.querySelectorAll('details.facet'));
      chips.forEach((chip) => {
        chip.addEventListener('toggle', () => {
          if (!chip.open) return;
          chips.forEach((other) => {
            if (other !== chip) other.open = false;
          });
        });
      });

      document.addEventListener('click', (event) => {
        if (host.contains(event.target)) return;
        chips.forEach((chip) => {
          chip.open = false;
        });
      });
    }

    applyFilters(form) {
      const checked = {};
      form.querySelectorAll('input[data-fb-key]:checked').forEach((input) => {
        (checked[input.dataset.fbKey] = checked[input.dataset.fbKey] || []).push(input.value);
      });
      const minInput = form.querySelector('[data-fb-price="min"]');
      const maxInput = form.querySelector('[data-fb-price="max"]');
      const minRaw = minInput ? minInput.value : '';
      const maxRaw = maxInput ? maxInput.value : '';
      const min = parseFloat(minRaw);
      const max = parseFloat(maxRaw);

      // Mirror the choice onto the other host so the hidden copy never drifts
      // out of sync with what is on screen.
      (this.forms || []).forEach((other) => {
        if (other === form) return;
        other.querySelectorAll('input[data-fb-key]').forEach((input) => {
          const match = form.querySelector(
            `input[data-fb-key="${input.dataset.fbKey}"][value="${CSS.escape(input.value)}"]`
          );
          if (match) input.checked = match.checked;
        });
        const otherMin = other.querySelector('[data-fb-price="min"]');
        const otherMax = other.querySelector('[data-fb-price="max"]');
        if (otherMin) otherMin.value = minRaw;
        if (otherMax) otherMax.value = maxRaw;
      });

      let shown = 0;
      this.items.forEach((li) => {
        const amount = parseFloat(li.dataset.amount) || 0;
        const matches =
          Object.entries(checked).every(([key, values]) =>
            values.includes(key === 'vendor' ? (li.dataset.vendor || '').trim() : li.dataset[key])) &&
          (Number.isNaN(min) || amount >= min) &&
          (Number.isNaN(max) || amount <= max);
        li.hidden = !matches;
        if (matches) shown += 1;
      });

      // Nothing matched, so the filters have hidden every card. Say so rather
      // than leaving an empty column.
      const grid = this.querySelector('.search-fallback__grid');
      if (grid) grid.classList.toggle('search-fallback__grid--empty', shown === 0);
    }

    /**
     * The "no results" message and the "0 products" toolbar both contradict a
     * panel that is about to list products, so they come out together.
     */
    get replaceable() {
      if (this.dataset.replaceEmpty !== 'true') return [];
      const scope = this.closest('facet-filters') || document;
      return ['.collection-empty', '.collection-toolbar']
        .map((selector) => scope.querySelector(selector))
        .filter(Boolean);
    }

    hideEmptyState() {
      this.replaced = this.replaceable.map((node) => {
        const previous = node.style.display;
        node.style.display = 'none';
        return { node, previous };
      });
    }

    restoreEmptyState() {
      (this.replaced || []).forEach(({ node, previous }) => {
        node.style.display = previous;
      });
      this.replaced = [];
    }
  }
  customElements.define('search-fallback', SearchFallback);

  /* Mobile search overlay — focus the field when it opens */
  const searchModal = document.getElementById('search-modal');
  if (searchModal) {
    searchModal.addEventListener('overlay:open', () => {
      const field = searchModal.querySelector('input[type="search"]');
      if (field) requestAnimationFrame(() => field.focus());
    });
  }

  /* ------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------ */
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    const observeAll = () => document.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => observer.observe(el));
    observeAll();
    document.addEventListener('shopify:section:load', observeAll);
    /* Safety net: if the observer never fires (a stalled main thread, a browser
       quirk), nothing may stay hidden. Reveal whatever is still pending. */
    setTimeout(() => document.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => el.classList.add('is-visible')), 2500);
  } else {
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
  }

  /* ------------------------------------------------------------------
     Light / dark mode. The attribute is set before first paint by the inline
     script in layout/theme.liquid; this only flips it and remembers the choice.
     ------------------------------------------------------------------ */
  const ThemeMode = {
    KEY: '3afret_theme',
    current() { return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'; },
    apply(mode, remember) {
      document.documentElement.setAttribute('data-theme', mode);
      if (remember) { try { localStorage.setItem(this.KEY, mode); } catch (e) { /* private mode */ } }
      const dark = mode === 'dark';
      document.querySelectorAll('[data-theme-toggle]').forEach((btn) => {
        btn.setAttribute('aria-pressed', dark ? 'true' : 'false');
        if (btn.getAttribute('role') === 'switch') { btn.setAttribute('aria-checked', dark ? 'true' : 'false'); return; }
        const label = dark ? 'Switch to light mode' : 'Switch to dark mode';
        btn.setAttribute('aria-label', label); btn.setAttribute('title', label);
        const text = btn.querySelector('[data-theme-toggle-label]');
        if (text) text.textContent = dark ? 'Light mode' : 'Dark mode';
      });
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) {
        if (dark) { if (!meta.dataset.light) meta.dataset.light = meta.content; meta.content = '#0b1020'; }
        else if (meta.dataset.light) meta.content = meta.dataset.light;
      }
      document.dispatchEvent(new CustomEvent('theme:mode', { detail: { mode } }));
    },
    init() {
      this.apply(this.current(), false);
      document.addEventListener('click', (event) => {
        const btn = Theme.closest(event.target, '[data-theme-toggle]');
        if (!btn) return;
        event.preventDefault();
        this.apply(this.current() === 'dark' ? 'light' : 'dark', true);
      });
      /* Follow the device only while the visitor has not chosen for themselves. */
      if (window.matchMedia) {
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
          let saved = null; try { saved = localStorage.getItem(this.KEY); } catch (err) {}
          if (!saved && document.documentElement.hasAttribute('data-theme')) this.apply(e.matches ? 'dark' : 'light', false);
        });
      }
    }
  };
  if (document.documentElement.hasAttribute('data-theme')) ThemeMode.init();
  Theme.mode = ThemeMode;

  /* ------------------------------------------------------------------
     Details accordion: animate open / close (~200ms). Falls back to the
     native instant toggle when the visitor prefers reduced motion.
     ------------------------------------------------------------------ */
  const Accordion = {
    reduced() {
      return (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) || document.documentElement.classList.contains('motion-off');
    },
    settle(details, content, after) {
      let done = false;
      const finish = () => {
        if (done) return; done = true;
        content.removeEventListener('transitionend', finish);
        content.style.height = ''; content.style.opacity = ''; content.style.transition = '';
        details.classList.remove('is-animating');
        if (after) after();
      };
      content.addEventListener('transitionend', finish);
      setTimeout(finish, 320);
    },
    open(details, content) {
      details.classList.add('is-animating');
      details.open = true;
      const target = content.scrollHeight;
      content.style.height = '0px'; content.style.opacity = '0';
      requestAnimationFrame(() => {
        content.style.transition = 'height 220ms cubic-bezier(.4,0,.2,1), opacity 180ms ease';
        content.style.height = target + 'px'; content.style.opacity = '1';
      });
      this.settle(details, content);
    },
    close(details, content) {
      details.classList.add('is-animating');
      content.style.height = content.scrollHeight + 'px'; content.style.opacity = '1';
      requestAnimationFrame(() => {
        content.style.transition = 'height 200ms cubic-bezier(.4,0,.2,1), opacity 150ms ease';
        content.style.height = '0px'; content.style.opacity = '0';
      });
      this.settle(details, content, () => { details.open = false; });
    },
    init() {
      document.addEventListener('click', (event) => {
        const summary = Theme.closest(event.target, 'details[data-accordion] > summary');
        if (!summary) return;
        const details = summary.parentElement;
        const content = details.querySelector(':scope > .accordion__content');
        if (!content || this.reduced()) return;            // native toggle
        event.preventDefault();
        if (details.classList.contains('is-animating')) return;
        if (details.open) this.close(details, content); else this.open(details, content);
      });
    }
  };
  Accordion.init();
  Theme.accordion = Accordion;

  /* ------------------------------------------------------------------
     Theme editor support
     ------------------------------------------------------------------ */
  document.addEventListener('shopify:block:select', (event) => {
    const slide = Theme.closest(event.target, '[data-slider-track] > *');
    if (slide && slide.parentElement) {
      slide.parentElement.scrollTo({ left: slide.offsetLeft - slide.parentElement.offsetLeft, behavior: 'smooth' });
    }
  });
})();
